import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import AdminCard from '../../components/admin/AdminCard';
import adminAxios from '../../services/adminAxios';
import { formatDate } from '../../utils/dateHelper';
import '../../assets/css/admin/users.css';

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const usersPerPage = 10;

  const fetchUsers = useCallback(async () => {
    if (!loading) setLoading(true);
    try {
      // Fetch all users without pagination first to get complete data
      const response = await adminAxios.get(`/api/admin/users?include_stats=true`);
      
      if (response.data.success) {
        setUsers(response.data.users);
        setError(null);
      } else {
        setError('Failed to load users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Filter and search users
  useEffect(() => {
    let filtered = [...users];

    // Apply search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(user => 
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phone?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply user type filter
    if (filterType) {
      filtered = filtered.filter(user => 
        user.user_type?.toLowerCase() === filterType.toLowerCase()
      );
    }

    setFilteredUsers(filtered);
    
    // Calculate pagination for filtered results
    const pages = Math.ceil(filtered.length / usersPerPage);
    setTotalPages(pages);
    
    // Reset to first page when filters change
    if (currentPage > pages && pages > 0) {
      setCurrentPage(1);
    }
  }, [users, searchTerm, filterType, currentPage]);

  // Get current page users
  const getCurrentPageUsers = () => {
    const startIndex = (currentPage - 1) * usersPerPage;
    const endIndex = startIndex + usersPerPage;
    return filteredUsers.slice(startIndex, endIndex);
  };

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleTypeFilterChange = (e) => {
    setFilterType(e.target.value);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const currentPageUsers = getCurrentPageUsers();

  return (
    <AdminLayout>
      <div className="container-fluid px-4">
        <h1 className="mt-4">Users Management</h1>
        <ol className="breadcrumb mb-4">
          <li className="breadcrumb-item"><Link to="/admin/dashboard">Dashboard</Link></li>
          <li className="breadcrumb-item active">Users</li>
        </ol>
        
        <AdminCard 
          title={`All Users (${filteredUsers.length} ${filterType ? `${filterType} ` : ''}${searchTerm ? 'matching' : 'total'})`}
          headerActions={
            <div className="d-flex gap-2">
              <input 
                type="text"
                className="form-control form-control-sm"
                placeholder="Search users..."
                value={searchTerm}
                onChange={handleSearchChange}
                style={{ minWidth: '200px' }}
              />
              <select 
                className="form-select form-select-sm"
                value={filterType}
                onChange={handleTypeFilterChange}
                style={{ minWidth: '150px' }}
              >
                <option value="">All User Types</option>
                <option value="event_attendee">Event Attendees</option>
                <option value="event_promoter">Event Promoters</option>
                <option value="admin">Administrators</option>
              </select>
            </div>
          }
          noPadding
        >
          {loading ? (
            <div className="text-center p-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="alert alert-danger">{error}</div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover table-bordered users-table" style={{backgroundColor: 'var(--dark-bg-secondary)'}}>
                  <thead>
                    <tr style={{backgroundColor: 'var(--dark-bg-tertiary)'}}>
                      <th>User Type</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Joined</th>
                      <th>Events</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentPageUsers.length > 0 ? currentPageUsers.map(user => (
                      <tr key={user.id}>
                        <td>
                          <span className={`user-type-badge ${user.user_type || 'event_attendee'}`}>
                            {user.user_type === 'event_attendee' ? 'Attendee' : 
                             user.user_type === 'event_promoter' ? 'Promoter' : 
                             user.user_type === 'admin' ? 'Admin' : 'Unknown'}
                          </span>
                        </td>
                        <td className="user-name">{user.name || 'N/A'}</td>
                        <td>{user.email}</td>
                        <td>{user.phone || '—'}</td>
                        <td>{formatDate(user.created_at)}</td>
                        <td>
                          <span className="badge bg-info">
                            {user.events_count !== undefined ? user.events_count : '—'}
                          </span>
                        </td>
                        <td>
                          <Link to={`/admin/users/${user.id}`} className="btn btn-sm btn-outline-primary">
                            <i className="fa fa-eye me-1"></i> View
                          </Link>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="7" className="text-center py-4">
                          <div className="empty-state-inline">
                            <i className="fa fa-users me-2"></i>
                            <span>
                              {searchTerm || filterType 
                                ? 'No users match your search criteria' 
                                : 'No users found'
                              }
                            </span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-4 px-3 pb-3">
                  <div className="text-muted small">
                    Showing {((currentPage - 1) * usersPerPage) + 1} to {Math.min(currentPage * usersPerPage, filteredUsers.length)} of {filteredUsers.length} users
                  </div>
                  <nav>
                    <ul className="pagination mb-0">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                      </li>
                      
                      {[...Array(totalPages)].map((_, i) => (
                        <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                          <button 
                            className="page-link" 
                            onClick={() => handlePageChange(i + 1)}
                          >
                            {i + 1}
                          </button>
                        </li>
                      ))}
                      
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </>
          )}
        </AdminCard>
      </div>
    </AdminLayout>
  );
};

export default UsersPage;
